/**
 * Other Challenges Card
 * Additional ways to earn XP: Scan, Refer, Streak
 */

import { Scan, UserPlus, Flame, Zap, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { XP_VALUES } from '../utils/constants';

interface OtherChallengesCardProps {
  totalScans: number;
  totalReferrals: number;
  streak: number;
}

interface ChallengeRowProps {
  icon: React.ComponentType<{ className?: string }>;
  iconBg: string;
  title: string;
  description: string;
  xpLabel: string;
  stat?: string | number;
  statLabel?: string;
  linkTo?: string;
}

function ChallengeRow({
  icon: Icon,
  iconBg,
  title,
  description,
  xpLabel,
  stat,
  statLabel,
  linkTo,
}: ChallengeRowProps) {
  const content = (
    <div className="flex items-center gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors group">
      <div className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center shrink-0`}>
        <Icon className="w-6 h-6 text-white" />
      </div>

      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-gray-900">{title}</h4>
        <p className="text-sm text-gray-500 truncate">{description}</p>
      </div>

      <div className="flex items-center gap-4 shrink-0">
        {stat !== undefined && (
          <div className="text-right">
            <div className="text-lg font-semibold text-gray-900">{stat}</div>
            {statLabel && <div className="text-xs text-gray-500">{statLabel}</div>}
          </div>
        )}

        <div className="flex items-center gap-1 px-3 py-1.5 bg-accent/10 rounded-full">
          <Zap className="w-4 h-4 text-accent" />
          <span className="text-sm font-medium text-accent">{xpLabel}</span>
        </div>

        {linkTo && (
          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary-600 transition-colors" />
        )}
      </div>
    </div>
  );

  if (linkTo) {
    return <Link to={linkTo}>{content}</Link>;
  }

  return content;
}

export function OtherChallengesCard({
  totalScans,
  totalReferrals,
  streak,
}: OtherChallengesCardProps) {
  const { t } = useLanguage();

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-200">
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900">
          {t('challenges.other.title', 'Other Ways to Earn')}
        </h2>
        <p className="text-sm text-gray-500">
          {t('challenges.other.subtitle', 'Complete these activities for bonus XP')}
        </p>
      </div>

      <div className="divide-y divide-gray-100">
        <ChallengeRow
          icon={Scan}
          iconBg="bg-blue-500"
          title={t('challenges.other.scan.title', 'Scan As-Is Items')}
          description={t('challenges.other.scan.description', 'Scan items in the As-Is section at your local IKEA')}
          xpLabel={`+${XP_VALUES.SCAN_ITEM} XP`}
          stat={totalScans}
          statLabel={t('challenges.other.scan.stat', 'scanned')}
        />

        <ChallengeRow
          icon={UserPlus}
          iconBg="bg-purple-500"
          title={t('challenges.other.refer.title', 'Refer a Friend')}
          description={t('challenges.other.refer.description', 'Invite friends to join Rumio')}
          xpLabel={`+${XP_VALUES.REFERRAL} XP`}
          stat={totalReferrals}
          statLabel={t('challenges.other.refer.stat', 'referrals')}
          linkTo="/account/referrals"
        />

        <ChallengeRow
          icon={Flame}
          iconBg="bg-orange-500"
          title={t('challenges.other.streak.title', 'Daily Streak')}
          description={t('challenges.other.streak.description', 'Use Rumio every day to build your streak')}
          xpLabel={t('challenges.other.streak.badge', 'Badge at 7 days')}
          stat={streak}
          statLabel={t('challenges.other.streak.stat', 'day streak')}
        />
      </div>
    </div>
  );
}
